import { useState } from "react";
import { ago, api, can, Drawer, Empty, fmtDate, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";
import { AdviceTake, DeptPage } from "./dept";
import { usd } from "./finance";
import { ReleaseLink } from "./product";
import { NewTask, TaskDrawer, TaskRow } from "./work";

export const STAGES: Record<string, string> = {
  prospect: "Prospect", outreach: "Outreach", talking: "In talks", agreed: "Agreed", active: "Active", completed: "Completed", paused: "Paused", declined: "Declined",
};
const AGREEMENT: Record<string, { label: string; tone: string }> = {
  none: { label: "Nothing signed", tone: "warn" }, verbal: { label: "Verbal only", tone: "warn" }, drafted: { label: "Drafted", tone: "info" }, signed: { label: "Signed", tone: "good" },
};

export function AgreementPill({ o }: { o: any }) {
  const a = AGREEMENT[o.agreement_status] ?? AGREEMENT.none!;
  return <Pill tone={a.tone}>{a.label}</Pill>;
}

export function Partnerships() {
  const { me } = useMe();
  const route = useRoute();
  const { data, error, reload } = useLoad<any[]>("/api/orgs", [route.q.get("org")]);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("open");
  const rows = (data || []).filter((o) => (filter === "open" ? !["completed", "declined"].includes(o.stage) : filter === "all" ? true : o.kind === filter));
  const take = (
    <AdviceTake
      page="partnerships"
      ask="Ask Claude where to focus"
      intro="Claude reads every partner, their deliverables and the partnership tasks here, then tells you what needs doing this week, what's at risk and what to let go. About 10 cents a run."
    />
  );
  const toolbar = (
      <div className="row between">
        <div className="tabs">
          {[["open", "Current"], ["all", "All"], ["affiliate", "Affiliates"], ["influencer", "Influencers"]].map(([k, l]) => (
            <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k!)}>{l}</button>
          ))}
        </div>
        {can(me, "partners.edit") && <button className="btn primary" onClick={() => setAdding(true)}>Add partner</button>}
      </div>
  );
  return (
    <DeptPage area="partnerships" take={take} toolbar={toolbar}>
      <p className="muted small" style={{ margin: 0 }}>Nothing counts as a commitment until the agreement is signed and someone confirms it.</p>
      <section className="card" style={{ padding: 0 }}>
        {!data ? <div style={{ padding: 20 }}><Loading error={error} /></div> : rows.length ? (
          <div className="scroll-x">
            <table className="t">
              <thead><tr><th>Name</th><th>Stage</th><th>Agreement</th><th className="hide-sm">Owner</th><th className="hide-sm">Open deliverables</th></tr></thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => setQuery({ org: o.id })}>
                    <td><strong>{o.name}</strong><div className="muted tiny">{o.relationship}</div></td>
                    <td><Pill>{STAGES[o.stage]}</Pill></td>
                    <td><AgreementPill o={o} /></td>
                    <td className="hide-sm">{o.owner_name || <span className="muted">No owner</span>}</td>
                    <td className="hide-sm">{o.open_deliverables}{o.next_due ? ` · next ${fmtDate(o.next_due)}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ padding: 20 }}><Empty>Nothing here.</Empty></div>}
      </section>
      {route.q.get("org") && <OrgDrawer id={route.q.get("org")!} onClose={() => setQuery({ org: null })} onChange={reload} />}
      {route.q.get("task") && <TaskDrawer id={route.q.get("task")!} onClose={() => setQuery({ task: null })} onChange={reload} />}
      {adding && <OrgForm onClose={() => setAdding(false)} onSaved={(id) => (setAdding(false), reload(), setQuery({ org: id }))} />}
    </DeptPage>
  );
}

function OrgForm({ org, onClose, onSaved }: { org?: any; onClose: () => void; onSaved: (id: string) => void }) {
  const { me, toast } = useMe();
  const users = useLoad<any[]>("/api/users").data || [];
  const o = org || { kind: "partner", stage: "prospect", agreement_status: "none" };
  return (
    <Drawer title={org ? `Edit ${org.name}` : "Add partner"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, any>;
          fd.commitment_confirmed = fd.commitment_confirmed === "on";
          if (!can(me, "finance.edit")) {
            delete fd.commission_basis;
            delete fd.commission_rate;
          }
          try {
            const r = org ? await api(`/api/orgs/${org.id}`, { method: "PATCH", body: fd }) : await api("/api/orgs", { method: "POST", body: fd });
            toast("Saved");
            onSaved(org?.id ?? r.id);
          } catch (err: any) {
            toast(err.message, true);
          }
        }}
      >
        <label className="field">Name<input className="input" name="name" required defaultValue={o.name} /></label>
        <div className="form-2">
          <label className="field">Type<select className="input" name="kind" defaultValue={o.kind}>{["partner", "affiliate", "influencer", "vendor"].map((k) => <option key={k} value={k}>{k[0]!.toUpperCase() + k.slice(1)}</option>)}</select></label>
          <label className="field">Stage<select className="input" name="stage" defaultValue={o.stage}>{Object.entries(STAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label className="field">Owner<select className="input" name="owner_id" defaultValue={o.owner_id || ""}><option value="">No owner yet</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
          <label className="field">Agreement<select className="input" name="agreement_status" defaultValue={o.agreement_status}>{Object.entries(AGREEMENT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
          <label className="field">Starts<input className="input" type="date" name="starts_on" defaultValue={o.starts_on || ""} /></label>
          <label className="field">Ends<input className="input" type="date" name="ends_on" defaultValue={o.ends_on || ""} /></label>
        </div>
        <label className="field">Link to the agreement<input className="input" name="agreement_url" type="url" defaultValue={o.agreement_url || ""} placeholder="https://" /></label>
        <label className="field">What the relationship is<input className="input" name="relationship" defaultValue={o.relationship || ""} /></label>
        <div className="form-2">
          <label className="field">Offer to their customers<input className="input" name="customer_offer" defaultValue={o.customer_offer || ""} placeholder="e.g. 20% off" /></label>
          <label className="field">Revenue share (empty = none)<input className="input" name="rev_share" defaultValue={o.rev_share || ""} /></label>
          <label className="field">Referral or offer code<input className="input" name="referral_code" defaultValue={o.referral_code || ""} /></label>
          <label className="field">Website<input className="input" name="website" defaultValue={o.website || ""} /></label>
        </div>
        {can(me, "finance.edit") && (
          <div className="form-2">
            <label className="field">Commission basis<select className="input" name="commission_basis" defaultValue={o.commission_basis || ""}><option value="">None</option><option value="percent">Percent of revenue</option><option value="flat">Flat per paid subscriber</option></select></label>
            <label className="field">Commission rate<input className="input" type="number" step="any" name="commission_rate" defaultValue={o.commission_rate ?? ""} /></label>
          </div>
        )}
        <label className="field">Could be shared with them later<textarea className="input" name="shareable_summary" defaultValue={o.shareable_summary || ""} /></label>
        <label className="field">Internal notes (never shared)<textarea className="input" name="internal_notes" defaultValue={o.internal_notes || ""} /></label>
        {org && <label className="check"><input type="checkbox" name="commitment_confirmed" defaultChecked={!!o.commitment_confirmed} /> Confirmed commitment (needs a signed agreement)</label>}
        <div className="row"><button className="btn primary">Save</button></div>
      </form>
    </Drawer>
  );
}

function OrgDrawer({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { me, toast } = useMe();
  const { data: o, error, reload } = useLoad<any>(`/api/orgs/${id}`, [id]);
  const [edit, setEdit] = useState(false);
  const [newTask, setNewTask] = useState(false);
  const [form, setForm] = useState<"" | "contact" | "deliverable" | "ledger">("");
  if (!o) return <Drawer title="Partner" onClose={onClose}><Loading error={error} /></Drawer>;
  const post = async (path: string, body: any, msg: string) => {
    try {
      await api(path, { method: "POST", body });
      toast(msg);
      setForm("");
      reload();
      onChange();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const fdBody = (e: React.FormEvent<HTMLFormElement>) => (e.preventDefault(), Object.fromEntries(new FormData(e.currentTarget)) as Record<string, any>);
  return (
    <Drawer
      title={o.name}
      onClose={onClose}
      sub={<div className="row"><Pill>{STAGES[o.stage]}</Pill><AgreementPill o={o} />{o.commitment_confirmed ? <Pill tone="good">Confirmed commitment</Pill> : <Pill>Not a confirmed commitment</Pill>}</div>}
    >
      <section className="card stack">
        <div className="row between"><h2>About</h2>{o.can_edit && <button className="btn sm" onClick={() => setEdit(true)}>Edit</button>}</div>
        <p style={{ margin: 0 }}>{o.relationship || <span className="muted">Not described yet.</span>}</p>
        <div className="form-2 small">
          <div><div className="muted tiny">Owner</div>{o.owner_name || "No owner yet"}</div>
          <div><div className="muted tiny">Offer to their customers</div>{o.customer_offer || "None"}</div>
          <div><div className="muted tiny">Revenue share</div>{o.rev_share || "None"}</div>
          <div><div className="muted tiny">Code</div>{o.referral_code || "None yet"}</div>
          <div><div className="muted tiny">Dates</div>{o.starts_on ? `${fmtDate(o.starts_on)} to ${fmtDate(o.ends_on) || "?"}` : "Not set"}</div>
          {o.agreement_url && <div><div className="muted tiny">Agreement</div><a href={o.agreement_url} target="_blank" rel="noreferrer">Open</a></div>}
        </div>
        <ReleaseLink type="organization" id={id} canEdit={o.can_edit} />
        {o.internal_notes && <div className="banner info small"><div><strong>Internal:</strong> {o.internal_notes}</div></div>}
      </section>

      <section className="card stack">
        <div className="row between"><h2>Deliverables</h2>{o.can_edit && <button className="btn sm" onClick={() => setForm("deliverable")}>Add</button>}</div>
        {o.deliverables.length ? (
          <div className="list">
            {o.deliverables.map((d: any) => (
              <div key={d.id} className="item">
                <div className="grow">
                  <div className="title">{d.title}</div>
                  <div className="small muted">{d.party === "partner" ? `${o.name} owes this` : "Momeaze owes this"}{d.requirements ? ` · ${d.requirements}` : ""}</div>
                </div>
                {d.due_on ? <Pill tone={d.date_confirmed ? "" : "warn"}>{fmtDate(d.due_on)}{d.date_confirmed ? "" : " (unconfirmed)"}</Pill> : <Pill tone="warn">No date yet</Pill>}
                {o.can_edit ? (
                  <select className="input" style={{ width: "auto" }} value={d.status} onChange={async (e) => { await api(`/api/deliverables/${d.id}`, { method: "PATCH", body: { status: e.target.value } }); reload(); }}>
                    <option value="planned">Planned</option><option value="in_progress">In progress</option><option value="done">Done</option><option value="cancelled">Cancelled</option>
                  </select>
                ) : <Pill>{d.status}</Pill>}
              </div>
            ))}
          </div>
        ) : <span className="muted small">None yet.</span>}
        {form === "deliverable" && (
          <form className="form" onSubmit={(e) => post(`/api/orgs/${id}/deliverables`, { ...fdBody(e), date_confirmed: new FormData(e.currentTarget).get("date_confirmed") === "on" }, "Deliverable added")}>
            <label className="field">What<input className="input" name="title" required /></label>
            <div className="form-2">
              <label className="field">Who owes it<select className="input" name="party"><option value="momeaze">Momeaze</option><option value="partner">{o.name}</option></select></label>
              <label className="field">Due<input className="input" type="date" name="due_on" /></label>
            </div>
            <label className="check"><input type="checkbox" name="date_confirmed" /> Date confirmed with them</label>
            <label className="field">Content requirements<textarea className="input" name="requirements" /></label>
            <div className="row"><button className="btn primary">Add deliverable</button></div>
          </form>
        )}
      </section>

      <section className="card stack">
        <div className="row between"><h2>Contacts</h2>{o.can_edit && <button className="btn sm" onClick={() => setForm("contact")}>Add</button>}</div>
        {o.contacts.length ? o.contacts.map((c: any) => <div key={c.id} className="small"><strong>{c.name}</strong> {c.role && <span className="muted">· {c.role}</span>}<div className="muted">{[c.email, c.phone].filter(Boolean).join(" · ")}</div></div>) : <span className="muted small">None yet.</span>}
        {form === "contact" && (
          <form className="form" onSubmit={(e) => post(`/api/orgs/${id}/contacts`, fdBody(e), "Contact added")}>
            <div className="form-2">
              <label className="field">Name<input className="input" name="name" required /></label>
              <label className="field">Role<input className="input" name="role" /></label>
              <label className="field">Email<input className="input" name="email" type="email" /></label>
              <label className="field">Phone<input className="input" name="phone" /></label>
            </div>
            <div className="row"><button className="btn primary">Add contact</button></div>
          </form>
        )}
      </section>

      <section className="card stack">
        <div className="row between"><h2>Tasks</h2><button className="btn sm" onClick={() => setNewTask(true)}>Add task</button></div>
        {o.tasks.length ? <div className="list">{o.tasks.map((t: any) => <TaskRow key={t.id} t={t} onOpen={(tid) => setQuery({ org: null, task: tid })} />)}</div> : <span className="muted small">None.</span>}
      </section>

      {o.ledger && (
        <section className="card stack">
          <div className="row between"><h2>Referrals and commissions</h2>{can(me, "finance.edit") && <button className="btn sm" onClick={() => setForm("ledger")}>Record</button>}</div>
          <div className="grid-tight">
            <div className="stat"><span className="label">Conversions, estimated</span><span className={`value ${o.ledger.totals.conversions.estimated == null ? "unknown" : ""}`}>{o.ledger.totals.conversions.estimated ?? "Unknown"}</span></div>
            <div className="stat"><span className="label">Conversions, verified</span><span className={`value ${o.ledger.totals.conversions.verified == null ? "unknown" : ""}`}>{o.ledger.totals.conversions.verified ?? "Unknown"}</span></div>
            <div className="stat"><span className="label">Commission, verified</span><span className={`value ${o.ledger.totals.commission.verified == null ? "unknown" : ""}`}>{o.ledger.totals.commission.verified == null ? "None" : usd(o.ledger.totals.commission.verified)}</span></div>
          </div>
          <p className="muted tiny" style={{ margin: 0 }}>A tracked link or code use isn't proof of a paid subscription. "Verified" means a store report or payment record confirmed it.</p>
          {o.ledger.rows.map((r: any) => (
            <div key={r.id} className="small row between">
              <span>{r.period} · {r.kind} · <Pill tone={r.certainty === "verified" ? "good" : ""}>{r.certainty}</Pill> {r.quantity != null ? `${r.quantity}` : ""} {r.amount != null ? usd(r.amount) : ""}</span>
              <span className="muted">{r.source}</span>
            </div>
          ))}
          {form === "ledger" && (
            <form className="form" onSubmit={(e) => post(`/api/orgs/${id}/ledger`, fdBody(e), "Recorded")}>
              <div className="form-2">
                <label className="field">Type<select className="input" name="kind"><option value="conversion">Conversions</option><option value="commission">Commission earned</option><option value="adjustment">Adjustment</option><option value="payout">Payout</option></select></label>
                <label className="field">Certainty<select className="input" name="certainty"><option value="estimated">Estimated</option><option value="verified">Verified</option></select></label>
                <label className="field">Month<input className="input" type="month" name="period" required /></label>
                <label className="field">How many<input className="input" type="number" name="quantity" /></label>
                <label className="field">Amount (USD)<input className="input" type="number" step="0.01" name="amount" /></label>
                <label className="field">Payout status<select className="input" name="status"><option value="owed">Owed</option><option value="paid">Paid</option></select></label>
              </div>
              <label className="field">Source (needed for verified)<input className="input" name="source" placeholder="e.g. App Store Connect offer code report" /></label>
              <div className="row"><button className="btn primary">Save</button></div>
            </form>
          )}
        </section>
      )}

      {o.expenses && (
        <section className="card stack">
          <h2>Spending</h2>
          {o.expenses.length ? o.expenses.map((e: any) => <div key={e.id} className="small row between"><span>{e.month} · {e.vendor}</span><span>{e.status} · {usd(e.amount)}</span></div>) : <span className="muted small">None linked. Record spending in Finance and pick this partner.</span>}
        </section>
      )}

      <section className="card stack">
        <h2>History</h2>
        <div className="timeline">{o.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^\w+\./, "").replace(/_/g, " ")}</span></div>)}</div>
        {!o.activity.length && <span className="muted small">Entered from what Kayla shared on Nov 3.</span>}
      </section>
      {edit && <OrgForm org={o} onClose={() => setEdit(false)} onSaved={() => (setEdit(false), reload(), onChange())} />}
      {newTask && <NewTask link={{ type: "organization", id, label: o.name }} onClose={() => setNewTask(false)} onCreated={() => (setNewTask(false), reload())} />}
    </Drawer>
  );
}
